import { useState } from 'react';
import { Link } from 'react-router';
import { authErrorMessage, networkErrorMessage } from '@/lib/auth-error';
import GoogleSignInButton from '@/components/GoogleSignInButton';
import { authClient } from '../lib/auth-client';
import { useLandAfterAuth } from '@/lib/landing';

export default function SignupPage() {
  const { data: session } = authClient.useSession();
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  // Same rule as the login screen. With email verification switched off a
  // planner is signed in the moment they sign up, so this is the arrival that
  // matters and it must not skip first-time setup.
  useLandAfterAuth(session);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      const result = await authClient.signUp.email({
        name,
        email,
        password,
        callbackURL: `${window.location.origin}/verify-email?status=success`,
      });
      if (result.error) {
        setError(authErrorMessage(result, 'Sign up failed'));
        setLoading(false);
        return;
      }
      setSubmitted(true);
      setLoading(false);
    } catch {
      setError(networkErrorMessage());
      setLoading(false);
    }
  };

  return (
    <div className="flex min-h-screen items-center justify-center bg-background px-6">
      <div className="w-full max-w-sm space-y-8">
        <div className="text-center">
          <h1 className="display-tight text-3xl font-semibold">Begin your journey</h1>
          <p className="mt-2 text-sm text-muted-foreground">
            Create an account to start planning
          </p>
        </div>

        <div className="rounded-3xl bg-card p-8 shadow-ambient">
          {submitted ? (
            <div className="space-y-4 text-center">
              <h2 className="text-lg font-semibold">Check your email</h2>
              <p className="text-sm text-muted-foreground">
                We sent a verification link to <strong>{email}</strong>. Click it to activate your account.
              </p>
              <Link
                to="/login"
                className="inline-block text-sm font-medium text-secondary underline-offset-4 hover:underline"
              >
                Back to sign in
              </Link>
            </div>
          ) : (
          <form onSubmit={handleSubmit} className="space-y-5">
            {error && (
              <div className="rounded-2xl bg-destructive/10 p-3 text-sm text-destructive">
                {error}
              </div>
            )}

            <div className="space-y-2">
              <label htmlFor="name" className="label-caps text-xs text-muted-foreground">
                Name
              </label>
              <input
                id="name"
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                required
                className="flex h-10 w-full rounded-2xl border border-[var(--color-outline-variant)] bg-[var(--color-surface-container-lowest)] px-4 py-2 text-sm transition-all placeholder:text-muted-foreground focus-visible:bg-[var(--color-surface-bright)] focus-visible:shadow-(--shadow-input-focus) focus-visible:border-[var(--color-focus-ring)] focus-visible:ring-2 focus-visible:ring-[var(--color-focus-ring)] focus-visible:outline-none"
                placeholder="Your name"
              />
            </div>

            <div className="space-y-2">
              <label htmlFor="email" className="label-caps text-xs text-muted-foreground">
                Email
              </label>
              <input
                id="email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                className="flex h-10 w-full rounded-2xl border border-[var(--color-outline-variant)] bg-[var(--color-surface-container-lowest)] px-4 py-2 text-sm transition-all placeholder:text-muted-foreground focus-visible:bg-[var(--color-surface-bright)] focus-visible:shadow-(--shadow-input-focus) focus-visible:border-[var(--color-focus-ring)] focus-visible:ring-2 focus-visible:ring-[var(--color-focus-ring)] focus-visible:outline-none"
                placeholder="you@example.com"
              />
            </div>

            <div className="space-y-2">
              <label htmlFor="password" className="label-caps text-xs text-muted-foreground">
                Password
              </label>
              <input
                id="password"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                minLength={8}
                className="flex h-10 w-full rounded-2xl border border-[var(--color-outline-variant)] bg-[var(--color-surface-container-lowest)] px-4 py-2 text-sm transition-all placeholder:text-muted-foreground focus-visible:bg-[var(--color-surface-bright)] focus-visible:shadow-(--shadow-input-focus) focus-visible:border-[var(--color-focus-ring)] focus-visible:ring-2 focus-visible:ring-[var(--color-focus-ring)] focus-visible:outline-none"
                placeholder="At least 8 characters"
              />
            </div>

            <button
              type="submit"
              disabled={loading}
              className="btn-primary-gradient inline-flex h-11 w-full items-center justify-center rounded-full px-6 text-sm font-medium text-white shadow-ambient transition-opacity hover:opacity-90 disabled:pointer-events-none disabled:opacity-50"
            >
              {loading ? 'Creating account…' : 'Get started'}
            </button>

            <div className="flex items-center gap-3 pt-1">
              <span className="h-px flex-1 bg-[var(--color-outline-variant)]" />
              <span className="text-xs text-muted-foreground">or</span>
              <span className="h-px flex-1 bg-[var(--color-outline-variant)]" />
            </div>

            <GoogleSignInButton label="Sign up with Google" onError={setError} />
          </form>
          )}
        </div>

        <p className="text-center text-sm text-muted-foreground">
          Already have an account?{' '}
          <Link to="/login" className="font-medium text-secondary underline-offset-4 hover:underline">
            Sign in
          </Link>
        </p>
      </div>
    </div>
  );
}
